import React, {useCallback, useContext, useMemo, useRef, useState} from 'react';
import Product from 'components/products/product';
import {defaultsDeep, isEmpty, isEqual} from 'lodash';

const productsContext = {};
const ProductsContext = React.createContext(productsContext);

export function useProductsContext(props = {}) {
  return useContext(ProductsContext);
}

function useProductsContextHook(props = {}) {
  const {products, loading} = props;
  const [value, setValue] = useState(productsContext);
  const refCallbackMap = useRef({});

  const setProductsContext = useCallback(
    (data) => {
      setValue((value) => defaultsDeep(data, value));
    },
    [setValue],
  );

  const refCallback = useCallback(
    (product) => (refData) => {
      const {productQuery} = refData ?? {};

      setValue((currentValue) => {
        const data = {};

        if (
          productQuery &&
          (!isEqual(currentValue.productQuery?.[product.ApiRoute]?.enabled, productQuery?.enabled) ||
            !isEqual(currentValue.productQuery?.[product.ApiRoute]?.ApiRoute, productQuery?.ApiRoute))
        ) {
          data.productQuery = {[product.ApiRoute]: productQuery};
        }

        if (!isEmpty(data)) {
          return defaultsDeep(data, currentValue);
        }

        return currentValue;
      });
    },
    [setValue],
  );

  const getProductRefCallback = useCallback(
    (product) => {
      const refKey = `${product?.ApiRoute ?? 'unknown'}-${product?.ProductId ?? 'unknown'}`;

      if (!refCallbackMap.current[refKey]) {
        refCallbackMap.current[refKey] = refCallback(product);
      }

      return refCallbackMap.current[refKey];
    },
    [refCallback],
  );

  const refCallbacks = useMemo(
    () =>
      products.reduce((acc, product) => {
        acc[product.ProductId] = getProductRefCallback(product);
        return acc;
      }, {}),
    [products, getProductRefCallback],
  );

  return {
    ...value,
    setProductsContext,
    products,
    loading,
    refCallbacks,
  };
}

/**
 *
 * @param {object} props
 * @param {React.ReactNode|(()=>React.ReactNode)} props.children
 * @param {object} props.products
 * @param {?string} props.loading
 * @return {React.ReactNode}
 * @constructor
 */
export function ProductsContextProvider(props = {}) {
  const {children, products, renderProducts = true} = props;
  const {refCallbacks, ...context} = useProductsContextHook(props);

  return (
    <ProductsContext.Provider value={context}>
      {renderProducts &&
        products.map((product) => (
          <React.Fragment key={product.ProductId}>
            <Product product={product} loadin={context.loading} ref={refCallbacks[product.ProductId]} />
          </React.Fragment>
        ))}

      {children}
    </ProductsContext.Provider>
  );
}
